import type { ProfileResponse } from '@shared/schemas';

import { formatDateEn } from '@/utils/format';

export const EXPERT_VALIDATED_LABEL = 'Verified by Invyo Expert';

/**
 * What hovering the "Verified by Invyo Expert" check says (#671, #719).
 *
 * Everyone reads the label alone, which names no checked data; the Invyo team
 * also reads who validated the profile and when. The API only sends those two
 * to an admin, so their presence is the switch, not a role check here. A
 * deleted validator leaves the date.
 */
export function expertValidationTooltip(
  profile: Pick<ProfileResponse, 'expert_validated_at' | 'expert_validated_by_name'>,
): string {
  const { expert_validated_at: at, expert_validated_by_name: by } = profile;
  if (!at) return EXPERT_VALIDATED_LABEL;
  const stamp = by
    ? `Validated by ${by} on ${formatDateEn(at)}.`
    : `Validated on ${formatDateEn(at)}.`;
  return `${EXPERT_VALIDATED_LABEL}. ${stamp}`;
}

/**
 * The check an admin clicks (#705, #719): the mark and the action in one, on
 * the list's Profiles tab and on the profile page's identity band (#754).
 * Hovering it says what a click does and, on a validated profile, who
 * validated it and when.
 */
export function expertValidationToggleTooltip(
  profile: Pick<
    ProfileResponse,
    'is_expert_validated' | 'expert_validated_at' | 'expert_validated_by_name'
  >,
): string {
  if (!profile.is_expert_validated) return 'Validate profile';
  const { expert_validated_at: at, expert_validated_by_name: by } = profile;
  const stamp = !at
    ? `${EXPERT_VALIDATED_LABEL}.`
    : by
      ? `Validated by ${by} on ${formatDateEn(at)}.`
      : `Validated on ${formatDateEn(at)}.`;
  return `${stamp} Click to remove the validation.`;
}
